import {ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {BusinessStockDto, SaleOfferDto, SaleOfferRequest} from '../../../../models/generated-models';
import {ConfirmDialogService} from '../../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {TradeApiService} from '../trade-api.service';
import {TradeStore} from '../trade.store';
import {DEFAULT_TRADE_UNIT, TRADE_BASE} from '../trade.constants';
import {formatQuantityWithUnit, normalizeQuantityInput, parseQuantity} from '../trade-utils';

/** One pull of a line: how much comes out of which stock pool. */
export interface SellPull {
  poolName: string;
  quantity: string;
}

/** One product line of the offer: sold under `productName`, pulled from pools. */
export interface SellLine {
  productName: string;
  rows: SellPull[];
}

/**
 * Creates a sale offer (ใบขาย). The seller names what they sell and pulls the
 * quantity out of one or more of their own pools (one product per sale — the PO wants exactly
 * one line per offer; the API still accepts a list) — that is how two pools the
 * system cannot know are the same product get combined, since only the owner
 * can decide that. The buyer never types a quantity.
 */
@Component({
  selector: 'app-trade-sell',
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: './trade-sell.component.html',
  styleUrl: './trade-sell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeSellComponent implements OnInit {
  private readonly api = inject(TradeApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);
  protected readonly store = inject(TradeStore);

  /** The only unit in the system. */
  protected readonly unit = DEFAULT_TRADE_UNIT;

  protected readonly stock = signal<BusinessStockDto[]>([]);
  protected readonly lines = signal<SellLine[]>([emptyLine()]);
  protected readonly saving = signal(false);
  protected readonly submitted = signal(false);
  protected note = '';

  protected readonly formatQuantityWithUnit = formatQuantityWithUnit;

  protected readonly totalQuantity = computed(
    () => this.lines().reduce((sum, _line, index) => sum + this.lineTotal(index), 0)
  );

  protected readonly canSubmit = computed(() => {
    const lines = this.lines();
    return this.store.selectedBusinessId() != null
      && lines.length > 0
      && lines.every((line, lineIndex) =>
        !!line.productName.trim()
        && line.rows.length > 0
        && this.lineTotal(lineIndex) > 0
        && line.rows.every((row, rowIndex) =>
          !!row.poolName
          && parseQuantity(row.quantity) > 0
          && !this.rowExceedsStock(lineIndex, rowIndex)));
  });

  constructor() {
    // Zoneless: the free stock follows the selected business.
    effect(() => {
      const businessId = this.store.selectedBusinessId();
      if (businessId == null) {
        this.stock.set([]);
        return;
      }
      this.api.listStock(businessId).subscribe({
        next: (items) => {
          // A pool with nothing left cannot be pulled from, so it is not offered at all.
          this.stock.set((items ?? []).filter((item) => Number(item.quantity ?? 0) > 0));
          this.applyInitialPool();
        },
        error: () => this.stock.set([])
      });
    });
  }

  /** The lot that was just recorded, when the harvest form handed over to this page. */
  protected readonly fromRecordId = signal<number | null>(null);
  protected readonly tradeBase = TRADE_BASE;

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const productName = params.get('productName')?.trim() ?? '';
    const quantity = normalizeQuantityInput(params.get('quantity') ?? '');
    const fromRecord = Number(params.get('fromRecord'));
    this.fromRecordId.set(Number.isInteger(fromRecord) && fromRecord > 0 ? fromRecord : null);
    if (productName) {
      // The clicked pool (or the lot just recorded) names the line and fills its first pull.
      this.lines.set([{productName, rows: [{poolName: productName, quantity: parseQuantity(quantity) > 0 ? quantity : ''}]}]);
    }
    void this.store.loadBusinesses();
  }

  /** The free stock of a pool. */
  protected availableStock(poolName: string): number {
    return parseQuantity(this.stock().find((pool) => pool.productName === poolName)?.quantity);
  }

  /** How much of a pool the whole form pulls — a pool may feed several lines. */
  protected poolUsage(poolName: string): number {
    if (!poolName) {
      return 0;
    }
    return this.lines().reduce(
      (sum, line) => sum + line.rows
        .filter((row) => row.poolName === poolName)
        .reduce((rowSum, row) => rowSum + parseQuantity(row.quantity), 0),
      0
    );
  }

  /** Over-pull is reported on every row of the offending pool. */
  protected rowExceedsStock(lineIndex: number, rowIndex: number): boolean {
    const row = this.lines()[lineIndex]?.rows[rowIndex];
    if (!row?.poolName) {
      return false;
    }
    return this.poolUsage(row.poolName) > this.availableStock(row.poolName);
  }

  protected lineTotal(lineIndex: number): number {
    return (this.lines()[lineIndex]?.rows ?? [])
      .reduce((sum, row) => sum + parseQuantity(row.quantity), 0);
  }

  /** A pool may appear once per line, so the others are disabled in its selects. */
  protected isPoolTaken(lineIndex: number, rowIndex: number, poolName: string): boolean {
    const rows = this.lines()[lineIndex]?.rows ?? [];
    return rows.some((row, index) => index !== rowIndex && row.poolName === poolName);
  }

  protected onProductNameInput(): void {
    this.touch();
  }

  protected onPoolChange(lineIndex: number, rowIndex: number, poolName: string): void {
    this.lines()[lineIndex].rows[rowIndex].poolName = poolName;
    this.touch();
  }

  /** Convenience: sell everything the pool holds. */
  protected useAllStock(lineIndex: number, rowIndex: number): void {
    const row = this.lines()[lineIndex].rows[rowIndex];
    row.quantity = String(this.availableStock(row.poolName));
    this.onQuantityInput(lineIndex, rowIndex);
  }

  protected onQuantityInput(lineIndex: number, rowIndex: number): void {
    const row = this.lines()[lineIndex].rows[rowIndex];
    row.quantity = normalizeQuantityInput(row.quantity);
    this.touch();
  }

  /** Adds another pull to a line — the "combine two pools" gesture. */
  protected addRow(lineIndex: number): void {
    const line = this.lines()[lineIndex];
    const taken = line.rows.map((row) => row.poolName);
    const next = this.stock().find((pool) => !taken.includes(pool.productName ?? ''));
    line.rows = [...line.rows, {poolName: next?.productName ?? '', quantity: ''}];
    this.touch();
  }

  protected removeRow(lineIndex: number, rowIndex: number): void {
    const line = this.lines()[lineIndex];
    line.rows = line.rows.filter((_, index) => index !== rowIndex);
    this.touch();
  }

  protected submit(): void {
    this.submitted.set(true);
    if (this.saving() || !this.canSubmit()) {
      return;
    }
    const lines = this.lines();

    void firstValueFrom(this.confirmDialog.open({
      title: 'ยืนยันการสร้างใบขาย',
      content: `สร้างใบขาย ${lines.length} รายการ `
        + `รวม ${formatQuantityWithUnit(this.totalQuantity(), this.unit)} `
        + `ในนาม ${this.store.selectedBusiness()?.title ?? ''} หรือไม่`,
      confirmButtonMsg: 'ยืนยัน',
      closeButtonMsg: 'ยกเลิก'
    })).then((confirmed) => {
      if (confirmed) {
        this.save();
      }
    }).catch(() => undefined);
  }

  private save(): void {
    this.saving.set(true);
    const request: SaleOfferRequest = {
      businessId: this.store.selectedBusinessId() ?? undefined,
      items: this.lines().map((line) => ({
        productName: line.productName.trim(),
        components: line.rows.map((row) => ({
          productName: row.poolName,
          quantity: parseQuantity(row.quantity)
        }))
      })),
      note: this.note.trim() || undefined
    };
    this.api.createOffer(request).subscribe({
      next: (offer: SaleOfferDto) => {
        this.saving.set(false);
        void this.router.navigate([TRADE_BASE, 'offers', offer.id]);
      },
      error: () => this.saving.set(false)
    });
  }

  /** Without a `productName` query the form starts on the first pool. */
  private applyInitialPool(): void {
    const first = this.lines()[0];
    if (first && !first.productName && !first.rows[0]?.poolName) {
      const poolName = this.stock()[0]?.productName ?? '';
      first.productName = poolName;
      first.rows[0] = {poolName, quantity: ''};
      this.touch();
    }
  }

  /** ngModel writes into the line objects; a new array makes the computeds re-run. */
  private touch(): void {
    this.lines.update((lines) => [...lines]);
  }
}

function emptyLine(): SellLine {
  return {productName: '', rows: [{poolName: '', quantity: ''}]};
}
